import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Header from '../components/Header'
import Footer from '../components/Footer'
import ConfigPanel from '../sections/ConfigPanel'
import PreviewArea from '../sections/PreviewArea'
import ActionBar from '../sections/ActionBar'
import { useHanziStore } from '../hooks/useHanziConfig'

const Home: React.FC = () => {
  const { t } = useTranslation()
  const config = useHanziStore((state) => state.config)
  const [showMobilePanel, setShowMobilePanel] = useState(false)
  const isPinyinAbove = config.showPinyin

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        if (e.key === 'p') {
          e.preventDefault()
          window.print()
        }
        if (e.key === 's') {
          e.preventDefault()
          const canvases = document.querySelectorAll('canvas')
          if (canvases.length === 0) return
          if (canvases.length === 1) {
            const link = document.createElement('a')
            link.download = `chinese-practice-${Date.now()}.png`
            link.href = canvases[0].toDataURL('image/png')
            link.click()
            return
          }
          canvases.forEach((canvas, index) => {
            const link = document.createElement('a')
            link.download = `chinese-practice-${Date.now()}-page-${index + 1}.png`
            link.href = canvas.toDataURL('image/png')
            link.click()
          })
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <Header />

      <main className="flex-1 flex flex-col lg:flex-row overflow-hidden relative">
        <aside
          className={`fixed inset-y-0 left-0 z-40 w-80 bg-white border-r border-border p-4 overflow-y-auto transform transition-transform duration-300 lg:static lg:transform-none lg:w-80 xl:w-96 lg:flex-shrink-0 lg:p-6 ${
            showMobilePanel ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <ConfigPanel />
        </aside>

        {showMobilePanel && (
          <div
            className="fixed inset-0 bg-black/30 z-30 lg:hidden"
            onClick={() => setShowMobilePanel(false)}
          />
        )}

        <button
          onClick={() => setShowMobilePanel(true)}
          className="fixed bottom-4 left-4 z-20 lg:hidden bg-primary text-white w-12 h-12 rounded-full shadow-lg flex items-center justify-center"
          aria-label={t('nav.settings')}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>

        <section className="flex-1 flex flex-col bg-background overflow-hidden">
          <div className={`flex-1 overflow-y-auto ${isPinyinAbove ? 'p-5 pb-4 lg:p-7 lg:pb-5' : 'p-4 pb-[10px] lg:p-6 lg:pb-[10px]'}`}>
            <PreviewArea config={config} />
          </div>
          <ActionBar />
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default Home
